/**
 * Componente que muestra el mapa de progreso global del nivel
 * Muestra todas las unidades y lecciones con su estado (completada, en progreso, bloqueada)
 */

import React from 'react';
import { LevelProgress } from '../../services/academicService';
import './LevelProgressMap.css';

interface LevelProgressMapProps {
  level: string;
  progress: LevelProgress[];
  currentUnit?: number;
  currentLesson?: number;
  onLessonSelect?: (unit: number, lesson: number) => void;
  onShowResults?: (unit: number, lesson: number) => void; // Callback para mostrar resultados
}

const LevelProgressMap: React.FC<LevelProgressMapProps> = ({
  level,
  progress,
  currentUnit,
  currentLesson,
  onLessonSelect,
  onShowResults,
}) => {
  // Agrupar progreso por unidad
  const progressByUnit = new Map<number, LevelProgress[]>();
  progress.forEach(p => {
    if (!progressByUnit.has(p.unit)) {
      progressByUnit.set(p.unit, []);
    }
    progressByUnit.get(p.unit)!.push(p);
  });

  // Ordenar unidades
  const units = Array.from(progressByUnit.keys()).sort((a, b) => a - b);

  const getStatusIcon = (lesson: LevelProgress): string => {
    if (lesson.completed) return '✅';
    if (lesson.inProgress) return '🔄';
    if (!lesson.unlocked) return '🔒';
    return '📖'; // Lección desbloqueada pero no iniciada
  };

  const getStatusClass = (lesson: LevelProgress): string => {
    if (lesson.completed) return 'lesson-completed';
    if (lesson.inProgress) return 'lesson-in-progress';
    if (!lesson.unlocked) return 'lesson-locked';
    return 'lesson-unlocked'; // Lección desbloqueada pero no iniciada
  };
  
  const isLessonClickable = (lesson: LevelProgress): boolean => {
    return lesson.unlocked || lesson.inProgress || lesson.completed;
  };

  const isCurrentLesson = (unit: number, lesson: number): boolean => {
    return currentUnit === unit && currentLesson === lesson;
  };

  return (
    <div className="level-progress-map">
      <div className="level-progress-map-content">
        {units.map(unit => {
          const lessons = progressByUnit.get(unit)!.sort((a, b) => a.lesson - b.lesson);
          const completedCount = lessons.filter(l => l.completed).length;
          const totalCount = lessons.length;

          return (
            <div key={unit} className="unit-section">
              <div className="unit-header">
                <span className="unit-title">Unidad {unit}</span>
                <span className="unit-progress">
                  {completedCount}/{totalCount}
                </span>
              </div>
              <div className="lessons-list">
                {lessons.map(lesson => {
                  const isClickable = isLessonClickable(lesson);
                  
                  
                  return (
                    <div
                      key={`${unit}-${lesson.lesson}`}
                      className={`lesson-item ${getStatusClass(lesson)} ${
                        isCurrentLesson(unit, lesson.lesson) ? 'current-lesson' : ''
                      } ${!isClickable ? 'lesson-disabled' : ''}`}
                      onClick={() => {
                        if (isClickable) {
                          onLessonSelect?.(unit, lesson.lesson);
                        }
                      }}
                      title={
                        lesson.completed
                          ? 'Completada'
                          : lesson.inProgress
                          ? 'En progreso'
                          : !lesson.unlocked
                          ? 'Bloqueada - Completa la lección anterior para desbloquear'
                          : 'Disponible'
                      }
                    >
                      <span className="lesson-icon">{getStatusIcon(lesson)}</span>
                      <span className="lesson-number">Lección {lesson.lesson}</span>
                      {lesson.completed && lesson.hasEvaluation && (
                        <>
                          <button
                            className="lesson-results-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onShowResults?.(unit, lesson.lesson);
                            }}
                            title="Ver resultados de evaluación"
                            aria-label="Ver resultados de evaluación"
                          >
                            📊
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LevelProgressMap;

